import moment from 'moment';
import { JSX, useEffect, useMemo, useState } from 'react';

import { CheckboxWithoutLabel } from 'Components/Checkbox/CheckboxWithoutLabel';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { TPRM_ASSESSMENT_DELEGATES, TPRM_ASSESSMENT_OWNER, UCRM_ASSESSMENT_DELEGATES, UCRM_ASSESSMENT_OWNER, UCRM_QUESTIONNAIRE_RESPONDENTS } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getDueDiligenceModuleReadWriteRole } from 'Helpers/UserUtils';
import { DueDiligenceAssessmentConfiguration, countApplicableDueDiligenceQuestions } from 'Models/DueDiligence';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { ThirdPartyContact, TprmSubmitInherentRiskQuestionnaireSelections } from 'Models/TPRM';
import { UcrmSubmitInherentRiskQuestionnaireSelections } from 'Models/Ucrm';
import { UserResponse } from 'Models/User';

import styles from './SubmitIrqSelections.module.css';

type ContactValidWithEmailAddress = ThirdPartyContact & { email_address: string };

/** The selections reported by `SubmitIrqSelections`, which satisfy `TprmSubmitInherentRiskQuestionnaireSelections` and `UcrmSubmitInherentRiskQuestionnaireSelections`. For UCRM, `third_party_contacts` is always empty; for TPRM, `questionnaire_respondent_ids` is always omitted. */
export type SubmitIrqSelectionsValue = TprmSubmitInherentRiskQuestionnaireSelections & UcrmSubmitInherentRiskQuestionnaireSelections;

interface SelectionsForAssessment {
    isSelected: boolean;
    assessmentDueDate?: Date;
    delegates: UserResponse[];
    owner: UserResponse;
    questionnaireRespondents: UserResponse[];
    questionnaireDueDate?: Date;
}

interface SubmitIrqSelectionsPropsBase {
    assessments: DueDiligenceAssessmentConfiguration[];
    inherentRiskRating: RiskRating;
    onSelectionsChanged: (selections: SubmitIrqSelectionsValue) => void;
}

/** @param contacts The third-party service contacts that can be invited to complete questionnaires. */
export interface TprmSubmitIrqSelectionsProps extends SubmitIrqSelectionsPropsBase {
    module: ProductModule.TPRM;
    contacts: ThirdPartyContact[];
}

/** @param defaultQuestionnaireRespondentIds The users who answer questionnaires by default (the use case's manager and delegates). */
export interface UcrmSubmitIrqSelectionsProps extends SubmitIrqSelectionsPropsBase {
    module: ProductModule.UCRM;
    defaultQuestionnaireRespondentIds: string[];
}

export type SubmitIrqSelectionsProps = TprmSubmitIrqSelectionsProps | UcrmSubmitIrqSelectionsProps;

/**
 * Centralizes UI for the selections that need to be made when submitting the IRQ.
 * Used on the IRQ page and the TPRM and UCRM Intake pages.
 *
 * `module` determines the module-specific UI:
 * * TPRM: a section for selecting the third-party service contacts to invite to complete questionnaires. `third_party_contacts` holds the selected contacts.
 * * UCRM: each selected assessment has a field for choosing its questionnaire respondents, `questionnaire_respondent_ids` is included in its selections, and the notification text describes respondents rather than third-party users. `third_party_contacts` is always empty, since use cases do not have contacts.
 */
export const SubmitIrqSelections = (props: SubmitIrqSelectionsProps) => {
    const users = useCachedData().users;
    const assigneeRolesRestriction = [getDueDiligenceModuleReadWriteRole(props.module)];
    const assessmentSubjectTerm = props.module === ProductModule.UCRM ? 'use case' : 'third-party service';
    const contacts = props.module === ProductModule.TPRM ? props.contacts : undefined;
    const defaultQuestionnaireRespondentIds = props.module === ProductModule.UCRM ? props.defaultQuestionnaireRespondentIds : [];

    // A type predicate narrows the filtered array to `ContactValidWithEmailAddress[]`, so `email_address` is non-optional wherever a valid contact is used.
    const validContacts = useMemo(() => (contacts ?? []).filter((contact): contact is ContactValidWithEmailAddress => contact.email_address !== undefined), [contacts]);
    const [selectedThirdPartyContacts, setSelectedThirdPartyContacts] = useState<ContactValidWithEmailAddress[]>(validContacts);

    // The number of questions each assessment would generate for the assessment subject's inherent risk rating.
    const assessmentQuestionCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const assessment of props.assessments) {
            counts[assessment.id] = countApplicableDueDiligenceQuestions(assessment, props.inherentRiskRating);
        }
        return counts;
    }, [props.assessments, props.inherentRiskRating]);

    const [selectionsForAssessments, setSelectionsForAssessments] = useState<Record<string, SelectionsForAssessment>>(() => {
        const selections: Record<string, SelectionsForAssessment> = {};
        for (const assessment of props.assessments) {
            selections[assessment.id] = {
                isSelected: assessment.is_default && assessmentQuestionCounts[assessment.id] > 0,
                delegates: users.filter((user) => assessment.default_delegate_ids.includes(user.cognito_subject)),
                owner: users.find((user) => user.cognito_subject === assessment.default_owner_id)!,
                questionnaireRespondents: users.filter((user) => defaultQuestionnaireRespondentIds.includes(user.cognito_subject)),
                questionnaireDueDate: assessment.expected_days_to_complete_questionnaire ? moment().add(assessment.expected_days_to_complete_questionnaire, 'days').toDate() : undefined,
                assessmentDueDate: assessment.expected_days_to_complete_assessment
                    ? moment()
                          .add(assessment.expected_days_to_complete_questionnaire ?? 0, 'days')
                          .add(assessment.expected_days_to_complete_assessment, 'days')
                          .toDate()
                    : undefined,
            };
        }
        return selections;
    });

    const atLeastOneAssessmentSelected = Object.values(selectionsForAssessments).some((selections) => selections.isSelected);

    // This use of `useEffect` is probably an anti-pattern, but it's acceptable for now.
    // Notify the parent whenever relevant state changes. `useEffect` ensures the parent is notified after all local state is updated after the execution of an event handler and setState.
    const onSelectionsChanged = useMemo(() => props.onSelectionsChanged, [props.onSelectionsChanged]);
    const includeQuestionnaireRespondents = props.module === ProductModule.UCRM;
    useEffect(
        () =>
            onSelectionsChanged({
                assessment_selections: Object.keys(selectionsForAssessments)
                    .filter((assessmentId) => selectionsForAssessments[assessmentId].isSelected)
                    .map((assessmentId) => ({
                        assessment_id: assessmentId,
                        assessment_due_date: selectionsForAssessments[assessmentId].assessmentDueDate ? jsDateToIso8601(selectionsForAssessments[assessmentId].assessmentDueDate) : undefined,
                        delegate_ids: selectionsForAssessments[assessmentId].delegates.map((delegate) => delegate.cognito_subject),
                        owner_id: selectionsForAssessments[assessmentId].owner.cognito_subject,
                        questionnaire_due_date: selectionsForAssessments[assessmentId].questionnaireDueDate ? jsDateToIso8601(selectionsForAssessments[assessmentId].questionnaireDueDate) : undefined,
                        ...(includeQuestionnaireRespondents && { questionnaire_respondent_ids: selectionsForAssessments[assessmentId].questionnaireRespondents.map((respondent) => respondent.cognito_subject) }),
                    })),
                third_party_contacts: atLeastOneAssessmentSelected ? selectedThirdPartyContacts : [],
            }),
        [selectedThirdPartyContacts, selectionsForAssessments, onSelectionsChanged, atLeastOneAssessmentSelected, includeQuestionnaireRespondents]
    );

    const updateAssessmentIsSelected = (assessmentId: string, isSelected: boolean) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.isSelected = isSelected;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const updateOwnerForAssessment = (assessmentId: string, user: UserResponse) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.owner = user;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const updateQuestionnaireDueDateForAssessment = (assessmentId: string, dueDate: Date | undefined) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.questionnaireDueDate = dueDate;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const updateControlAssessmentDueDateForAssessment = (assessmentId: string, dueDate: Date | undefined) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.assessmentDueDate = dueDate;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const updateDelegatesForAssessment = (assessmentId: string, delegates: UserResponse[]) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.delegates = delegates;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const updateQuestionnaireRespondentsForAssessment = (assessmentId: string, respondents: UserResponse[]) => {
        const newSelectionsForAssessments = { ...selectionsForAssessments };
        const selectionsForAssessment = newSelectionsForAssessments[assessmentId];
        selectionsForAssessment.questionnaireRespondents = respondents;
        setSelectionsForAssessments(newSelectionsForAssessments);
    };

    const contactColumns: ColumnConfig<ContactValidWithEmailAddress>[] = [
        {
            type: ColumnType.CHECKBOX,
            name: 'Selected',
            getIsChecked: (contact) => selectedThirdPartyContacts.includes(contact),
            getOnChecked: (contact) => (isChecked) => {
                setSelectedThirdPartyContacts((current) => {
                    if (isChecked) {
                        return [...current, contact];
                    } else {
                        return current.filter((c) => contact !== c);
                    }
                });
            },
        },
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (contact) => contact.name,
        },
        {
            type: ColumnType.STRING,
            name: 'Email',
            getString: (contact) => contact.email_address,
        },
    ];

    const contactsSection: JSX.Element = (() => {
        if (!atLeastOneAssessmentSelected || props.module !== ProductModule.TPRM) {
            return <></>;
        } else if (validContacts.length === 0) {
            return (
                <div>
                    <Text variant="Header3">Third-Party Service Contacts</Text>
                    <Text>There are no third-party service contacts with valid email addresses, so questionnaires cannot be sent to third-party users.</Text>
                </div>
            );
        } else {
            return (
                <div>
                    <Text variant="Header3">Third-Party Service Contacts</Text>
                    <Text>Select the third-party contacts to invite to the third-party portal, where the selected assessments can be completed. Each selected contact will receive an email with a link to access the portal. Only contacts with valid email addresses are listed here.</Text>
                    <Table<ContactValidWithEmailAddress> disableSorting columns={contactColumns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(contact) => contact.email_address} rowsData={validContacts} />
                </div>
            );
        }
    })();

    return (
        <div className={styles.sections}>
            <div>
                <Text variant="Header3">Due Diligence Assessments</Text>
                {props.assessments.length > 0 ? (
                    <>
                        <Text>{`Submitting the inherent risk questionnaire will generate selected due diligence assessments for the ${assessmentSubjectTerm}. Select the assessments that apply to this ${assessmentSubjectTerm} and, optionally, the due dates of the assessments.`}</Text>
                        <Text>{includeQuestionnaireRespondents ? "When an assessment is coming due, notifications will be sent to the assessment's owner and delegates. Each assessment's questionnaire respondents will be emailed when the assessment is generated." : "When an assessment is coming due, notifications will be sent to the assessment's owner and delegates. When the questionnaire corresponding to an assessment is coming due, notifications will be sent to all third-party users with access to the questionnaire."}</Text>
                    </>
                ) : (
                    <>
                        <Text>No due diligence assessments have been defined. It is highly recommended that a SummIT administrator define at least one assessment in Settings before completing due diligence workflows.</Text>
                    </>
                )}

                {props.assessments.map((assessment) => {
                    const selections = selectionsForAssessments[assessment.id];
                    const questionCount = assessmentQuestionCounts[assessment.id];
                    const hasNoQuestions = questionCount === 0;
                    return (
                        <div key={assessment.id} className={styles.assessmentSection}>
                            <div className={styles.assessmentHeaderRow}>
                                <CheckboxWithoutLabel disabled={hasNoQuestions} status={selections.isSelected ? 'checked' : 'unchecked'} onToggled={(isChecked) => updateAssessmentIsSelected(assessment.id, isChecked)} />
                                <Text noStyles variant="Header4">
                                    {assessment.name}
                                    <span className={styles.questionCount}>{` (${questionCount} ${questionCount === 1 ? 'question' : 'questions'})`}</span>
                                </Text>
                            </div>
                            {hasNoQuestions && (
                                <Text noStyles color="darkGray">
                                    {`This assessment cannot be selected because it does not have any questions applicable to the ${assessmentSubjectTerm}'s inherent risk rating.`}
                                </Text>
                            )}
                            {selections.isSelected && (
                                <>
                                    <div className={styles.fieldsRow}>
                                        <FormFieldUserSelect formFieldId={`${assessment.id}-owner`} formFieldLabel="Owner" rolesRestriction={assigneeRolesRestriction} notClearable required selectedUser={selections.owner} onUserSelected={(user?: UserResponse) => updateOwnerForAssessment(assessment.id, user!)} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_OWNER : TPRM_ASSESSMENT_OWNER} />
                                        <FormFieldUserMultiSelect formFieldId={`${assessment.id}-delegates`} formFieldLabel="Delegates" rolesRestriction={assigneeRolesRestriction} selectedUsers={selections.delegates} onUsersSelected={(users) => updateDelegatesForAssessment(assessment.id, users ?? [])} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_DELEGATES : TPRM_ASSESSMENT_DELEGATES} />
                                    </div>
                                    {includeQuestionnaireRespondents && (
                                        <div className={styles.fieldsRow}>
                                            <FormFieldUserMultiSelect formFieldId={`${assessment.id}-questionnaireRespondents`} formFieldLabel="Questionnaire Respondents" rolesRestriction={[Role.UCRM_USER]} selectedUsers={selections.questionnaireRespondents} onUsersSelected={(users) => updateQuestionnaireRespondentsForAssessment(assessment.id, users ?? [])} tooltip={UCRM_QUESTIONNAIRE_RESPONDENTS} />
                                        </div>
                                    )}
                                    <div className={styles.fieldsRow}>
                                        <div className={styles.datePickerAndRecommendation}>
                                            <FormFieldDatePicker formFieldId={`${assessment.id}-questionnaireDueDate`} formFieldLabel="Questionnaire Due Date" selected={selections.questionnaireDueDate} handleChange={(value: Date | undefined | null) => updateQuestionnaireDueDateForAssessment(assessment.id, value ?? undefined)} dateFormat="MM/dd/yyyy" />
                                            {assessment.expected_days_to_complete_questionnaire && (
                                                <Text noStyles color="darkGray">
                                                    Recommended: {moment().add(assessment.expected_days_to_complete_questionnaire, 'days').format('MM/DD/YYYY')}
                                                </Text>
                                            )}
                                        </div>
                                        <div className={styles.datePickerAndRecommendation}>
                                            <FormFieldDatePicker formFieldId={`${assessment.id}-assessmentDueDate`} formFieldLabel="Assessment Due Date" selected={selections.assessmentDueDate} handleChange={(value: Date | undefined | null) => updateControlAssessmentDueDateForAssessment(assessment.id, value ?? undefined)} dateFormat="MM/dd/yyyy" />
                                            {assessment.expected_days_to_complete_assessment && (
                                                <Text noStyles color="darkGray">
                                                    Recommended:{' '}
                                                    {moment()
                                                        .add(assessment.expected_days_to_complete_questionnaire ?? 0, 'days')
                                                        .add(assessment.expected_days_to_complete_assessment, 'days')
                                                        .format('MM/DD/YYYY')}
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            {contactsSection}
        </div>
    );
};
